import { useEffect, useState } from "react";
import { Plus, Trash2, Crown, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import "../admin-affiliate-premium.css";

const BLANK = { name: "", description: "", commission_pct: 25, min_net_loss: 0, color: "gold" };
const COLORS = ["bronze", "silver", "gold", "platinum", "diamond"];

export default function AffiliateCommissionTiers() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const [accounts, setAccounts] = useState([]);
  const [assignForm, setAssignForm] = useState({ tier_id: "", user_id: "" });
  const [overrideForm, setOverrideForm] = useState({ user_id: "", commission_pct: 25, note: "" });

  const load = async () => {
    try {
      const r = await api.get("/admin/aff-commission-tiers"); setRows(r.data?.rows || []);
      const a = await api.get("/admin/aff-accounts"); setAccounts(a.data?.rows || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/admin/aff-commission-tiers", form); toast.success("Tier added"); setOpen(false); setForm(BLANK); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this tier?")) return;
    try { await api.delete(`/admin/aff-commission-tiers/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const assign = async () => {
    if (!assignForm.tier_id || !assignForm.user_id) return toast.error("Pick tier and affiliate");
    try { await api.post("/admin/aff-commission-tiers/assign", assignForm); toast.success("Tier assigned"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const override = async () => {
    if (!overrideForm.user_id) return toast.error("Pick affiliate");
    try { await api.post("/admin/aff-commission-tiers/override", overrideForm); toast.success("Override applied"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="ap-bg p-4 lg:p-6">
      <div className="flex items-end justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-amber-300 font-bold mb-1">COMPENSATION</div>
          <div className="text-3xl font-black ap-gold-text">Commission Tiers</div>
          <div className="text-white/55 text-sm mt-1">Configure tier ladder and per-affiliate overrides.</div>
        </div>
        <button onClick={() => setOpen(true)} className="ap-btn-gold inline-flex items-center gap-2 px-4 h-10 rounded-xl text-[12px] font-bold" data-testid="new-tier-btn">
          <Plus size={14} /> New Tier
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-5">
        {rows.map((t) => (
          <div key={t.id} className="ap-glass" style={{ padding: 18, border: `1px solid rgba(212,175,55,0.35)` }}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Crown size={16} className="text-amber-300" />
                <div className="font-black text-white text-lg">{t.name}</div>
              </div>
              <button onClick={() => remove(t.id)} className="ap-btn-ghost p-1.5 rounded-md text-rose-300"><Trash2 size={12} /></button>
            </div>
            <div className="text-white/55 text-[12.5px] mb-2">{t.description}</div>
            <div className="text-amber-100 font-black text-2xl">{t.commission_pct}%</div>
            <div className="text-white/45 text-[11px] mt-0.5">Net loss ≥ ৳{Number(t.min_net_loss).toLocaleString()}</div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="ap-glass" style={{ padding: 18 }}>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold mb-2">Assign tier to affiliate</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <select value={assignForm.tier_id} onChange={(e) => setAssignForm({ ...assignForm, tier_id: e.target.value })} className="ap-input">
              <option value="">— Select tier —</option>
              {rows.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.commission_pct}%)</option>)}
            </select>
            <select value={assignForm.user_id} onChange={(e) => setAssignForm({ ...assignForm, user_id: e.target.value })} className="ap-input">
              <option value="">— Select affiliate —</option>
              {accounts.map((a) => <option key={a.user_id} value={a.user_id}>{a.username}</option>)}
            </select>
          </div>
          <button onClick={assign} className="ap-btn-gold mt-3 px-4 h-10 rounded-xl text-[12px] font-bold" data-testid="assign-tier-btn">Assign</button>
        </div>
        <div className="ap-glass" style={{ padding: 18 }}>
          <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold mb-2">Custom commission override</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={overrideForm.user_id} onChange={(e) => setOverrideForm({ ...overrideForm, user_id: e.target.value })} className="ap-input sm:col-span-2">
              <option value="">— Select affiliate —</option>
              {accounts.map((a) => <option key={a.user_id} value={a.user_id}>{a.username}</option>)}
            </select>
            <input type="number" min="0" max="80" value={overrideForm.commission_pct} onChange={(e) => setOverrideForm({ ...overrideForm, commission_pct: Number(e.target.value) })} className="ap-input" placeholder="%" />
          </div>
          <input value={overrideForm.note} onChange={(e) => setOverrideForm({ ...overrideForm, note: e.target.value })} className="ap-input mt-2" placeholder="Note (optional)" />
          <button onClick={override} className="ap-btn-gold mt-3 px-4 h-10 rounded-xl text-[12px] font-bold" data-testid="override-commission-btn">Apply Override</button>
        </div>
      </div>

      {open && (
        <div className="ap-modal-overlay" onClick={() => setOpen(false)}>
          <div className="ap-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-amber-300 font-bold">NEW TIER</div>
              <button onClick={() => setOpen(false)} className="ap-btn-ghost p-2 rounded-lg"><X size={14} /></button>
            </div>
            <h3 className="text-xl font-black ap-gold-text mb-3">Commission Tier</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ap-input" /></Field>
              <Field label="Color">
                <select value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="ap-input">
                  {COLORS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Commission %"><input type="number" value={form.commission_pct} onChange={(e) => setForm({ ...form, commission_pct: Number(e.target.value) })} className="ap-input" /></Field>
              <Field label="Min net loss (৳)"><input type="number" value={form.min_net_loss} onChange={(e) => setForm({ ...form, min_net_loss: Number(e.target.value) })} className="ap-input" /></Field>
              <div className="md:col-span-2">
                <Field label="Description"><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="ap-input resize-none" /></Field>
              </div>
            </div>
            <button onClick={save} className="ap-btn-gold mt-4 px-5 h-11 rounded-xl text-[13px] font-bold inline-flex items-center gap-2" data-testid="save-tier-btn">
              <Crown size={14} /> Create Tier
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) { return <div><div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55 mb-1.5">{label}</div>{children}</div>; }
